import React, { useMemo, useCallback } from 'react';
import { useBotDelete, useBotGetFilters, useBotList } from '../hooks/use-bot.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Bot } from '../types/bot.types';
import BotTable from '../components/bot-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message, Tag } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';

const BotTableContainer = () => {
  const { t } = useTranslation();

  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } = useBotGetFilters();

  const { data, isLoading, isFetching } = useBotList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteBotMutation = useBotDelete();

  const handleDeleteBot = useCallback(async (record: Bot) => {
    try {
      await deleteBotMutation.mutateAsync(record.id);
      message.success(t('crud.delete.success', { entity: t('bots.title') }));
    } catch (error) {
      message.error(t('crud.delete.error', { entity: t('bots.title') }));
    }
  }, []);

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  // Helper function to get bot status tag (same logic as in bot-control.component.tsx)
  const getBotStatusTag = useCallback(
    (statusDetails?: Bot['statusDetails']) => {
      if (!statusDetails) {
        return <Tag color="default">{t('bots.status.unknown') || 'Unknown'}</Tag>;
      }

      if (statusDetails.isPaused) {
        return <Tag color="orange">{t('bots.status.paused') || 'Paused'}</Tag>;
      }

      if (statusDetails.isRunning) {
        return <Tag color="green">{t('bots.status.running') || 'Running'}</Tag>;
      }

      return <Tag color="red">{t('bots.status.stopped') || 'Stopped'}</Tag>;
    },
    [t]
  );

  const columns = useMemo<ColumnConfig<Bot>[]>(
    () => [
      {
        title: t('bots.name.label'),
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: t('bots.job.label', { defaultValue: 'Job' }),
        key: 'job',
        render: (_: any, record: Bot) => record.job?.jobName || '-',
      },
      {
        title: t('bots.status.label'),
        key: 'status',
        render: (_: any, record: Bot) => getBotStatusTag(record.statusDetails),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_: any, record: Bot) => (
          <ActionButtons
            showEdit={rbac.hasAccess(userPermissions, 'bot.update')}
            showDelete={rbac.hasAccess(userPermissions, 'bot.delete')}
            showView={rbac.hasAccess(userPermissions, 'bot.view')}
            record={record}
            baseUrl="/dashboard/bots"
            onDelete={handleDeleteBot}
            isDeleting={deleteBotMutation.isPending && deleteBotMutation.variables === record.id}
          />
        ),
      },
    ],
    [handleDeleteBot, deleteBotMutation.isPending, deleteBotMutation.variables, t, getBotStatusTag]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('bots.title') })}
        createRoute="create"
        showCreateButton={rbac.hasAccess(userPermissions, 'bot.create')}
      />
      <div className="row mt-3">
        <BotTable
          data={tableData}
          columns={columns}
          loading={
            isLoading || deleteBotMutation.isPending || isFetching || isAvailableFiltersLoading
          }
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(BotTableContainer, 'bot.view');
